import { Alert, Button, Paper, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';

import { Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { UploadFiles } from '../../components/PowerlistSettings/UploadFiles';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  selectFileButton: {
    minWidth: 100,
    marginRight: theme.spacing(1),
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  agentCount: {
    flex: 1,
  },
}));

export interface IAgentUpload {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  teams: string[];
}

const tryTrim = (value) => (typeof value === 'string' ? value.trim() : value);

const cleanRow = (row) => {
  const cleanRow: any = {};
  const keys = Object.keys(row);
  for (const key of keys) {
    const cleanKey = key.replace(/ /g, '').toLowerCase();
    cleanRow[cleanKey] = tryTrim(row[key]);
  }
  return cleanRow;
};

const tryMapAgentFromRow = (row): IAgentUpload => ({
  id: uuidv4(),
  firstName: row.first || row.firstname,
  lastName: row.last || row.lastname,
  email: row.email || row.mail || row.emailaddress,
  teams: Array.isArray(row.teams)
    ? row.teams
    : typeof row.teams === 'string'
    ? row.teams.split(',').map((team) => team.trim())
    : [],
});

const hasAnyData = (agent) =>
  !!agent.firstName || !!agent.lastName || !!agent.email || !!agent.teams;

const forEachRow = (workbook, callback) => {
  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    for (const row of XLSX.utils.sheet_to_json<any>(sheet)) {
      if (row) callback(row);
    }
  }
};

const REQUIRED_HEADER_ROW = [
  'First Name',
  'Last Name',
  'Email',
  'Teams',
].map((header) => header.trim().toLowerCase());

export function AddAgentsUploadModal(props) {
  const classes = useStyles();

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const [agents, setAgents] = useState<IAgentUpload[]>([]);

  const onHide = () => {
    setAgents([]);

    props.onHide();
  };

  return (
    <Modal
      size="lg"
      style={{ display: 'block' }}
      aria-labelledby="example-modal-sizes-title-lg"
      show={props.show}
      onHide={onHide}
    >
      <Paper>
        <Modal.Header closeButton>
          <Modal.Title>Upload Agents</Modal.Title>
        </Modal.Header>
        <Formik
          onSubmit={(_values, _actions) => {
            props.addAgents(agents, props?.formik);
            props?.onHide();
          }}
          initialValues={{
            powerlistSpreadsheets: [],
          }}
        >
          {(formik) => (
            <Form onSubmit={formik.handleSubmit}>
              <Modal.Body>
                <div className={classes.container}>
                  <div className={classes.row}>
                    <Alert
                      severity="warning"
                      className={classes.alert}
                      icon={false}
                    >
                      Bulk upload a list of agents using{' '}
                      <a href="/media/onboarding/agents.csv" download>
                        this spreadsheet template
                      </a>
                      <br />
                      <br />
                      Column headers must be in the following format and order
                      exactly: First Name, Last Name, Email, Teams
                      <br />
                      Additional columns or altered spelling are not accepted
                    </Alert>
                  </div>
                  <div className={classes.row}>
                    <UploadFiles
                      formik={formik}
                      uploadFile={(file) => {
                        setAgents([]);

                        const reader = new FileReader();

                        reader.onload = (_e) => {
                          const workbook = XLSX.read(reader.result, {
                            type: !!reader.readAsBinaryString
                              ? 'binary'
                              : 'array',
                          });

                          const firstSheetName = workbook.SheetNames[0];
                          if (!firstSheetName) {
                            enqueueSnackbarWithStyles({
                              message:
                                'No worksheets in uploaded file. Please ensure file format.',
                              alertProps: { severity: 'error' },
                            });
                            return;
                          }

                          const worksheet = workbook.Sheets[firstSheetName];
                          if (!worksheet) {
                            enqueueSnackbarWithStyles({
                              message:
                                'No worksheets in uploaded file. Please ensure file format.',
                              alertProps: { severity: 'error' },
                            });
                            return;
                          }

                          const sheetData = XLSX.utils.sheet_to_json(
                            worksheet,
                            { header: 1 }
                          );

                          const headers = (sheetData.shift() as any[]).map(
                            (header) => header.trim().toLowerCase()
                          );

                          const missingRequiredHeaders = REQUIRED_HEADER_ROW.filter(
                            (value, i) => headers[i] !== value
                          );

                          const isMissingRequiredHeaders =
                            !headers?.length ||
                            headers?.length !== REQUIRED_HEADER_ROW.length ||
                            missingRequiredHeaders.length > 0;

                          if (isMissingRequiredHeaders) {
                            enqueueSnackbarWithStyles({
                              message: `Error reading file. Please only include the following columns exactly: First Name, Last Name, Email, Teams`,
                              alertProps: { severity: 'error' },
                            });
                            return;
                          }

                          const agents = {};

                          const tryAddAgent = (row) => {
                            const agent = tryMapAgentFromRow(cleanRow(row));

                            if (hasAnyData(agent)) {
                              agents[agent.email ?? agent.id] = agent;
                            }
                          };

                          forEachRow(workbook, tryAddAgent);

                          const newAgents: IAgentUpload[] = Object.values(
                            agents
                          );

                          setAgents(newAgents);

                          if (!newAgents.length) {
                            enqueueSnackbarWithStyles({
                              message:
                                'No agents found in uploaded file. Please ensure file format.',
                              alertProps: { severity: 'error' },
                            });
                          }
                        };

                        !!reader.readAsBinaryString
                          ? reader.readAsBinaryString(file)
                          : reader.readAsArrayBuffer(file);
                      }}
                    />
                  </div>
                  <div className={classes.row}>
                    Valid Formats: .xls, .xlsx, .csv
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                {!!agents?.length ? (
                  <div className={classes.agentCount}>
                    Uploading {agents?.length}{' '}
                    {agents?.length === 1 ? 'agent' : 'agents'} from your file
                  </div>
                ) : null}
                <Button
                  variant="contained"
                  disableElevation
                  className={classes.cancelButton}
                  onClick={onHide}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={formik.submitForm}
                  disabled={!agents.length}
                >
                  Save
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Paper>
    </Modal>
  );
}
